import { SnackbarForPromise } from 'annotation-tools/ui-parts';
import {
  combineLatestTyped,
  pending,
  promiseState,
  useBooleanState,
  useDataStream,
  useNavigator,
  useStreamValue,
  useValueAsStream,
  WithPromiseState
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { map, switchMap } from 'rxjs/operators';
import styled from 'styled-components';
import { deleteModelImage } from '../../../api/fetch-functions/model-image';
import { viewTexts } from '../../../constants/view-texts';
import { envMode } from '../../../env-values';
import { useSnackbar } from '../../../functions/hooks/use-snackbar';
import { useToken } from '../../../global-state/auth-state';
import { FetchListErrorReasonType } from '../../../global-state/database/common/error-reason-type';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { mapListLatest } from '../../../global-state/database/common/map-latest';
import {
  fetchModelImageListOf,
  modelImageListOf
} from '../../../global-state/database/model-image-list';
import { labelListOf } from '../../../global-state/database/model-label-list';
import { modelId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { routes } from '../../../routing/routes';
import { ICompanyType } from '../../../types/record/company';
import { IImageInfoType } from '../../../types/record/image-info';
import { IModelType } from '../../../types/record/model';
import { ConfirmDeleteDialogTemplate } from '../../dialogs/confirm-delete-dialog-template/confirm-delete-dialog-template';
import { snackbarMessages } from '../../rect-tool-with-crop/constants/messages/snackbar-message';
import { MessageArea } from '../../shared/message-area';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { useStartTrainingButtonHandler } from '../model-detail/use-start-training-button-handler';
import { ModelImageListView } from './model-image-list-view';
import { toMessageAreaValue } from './to-message-area-value';

const vt = viewTexts.dialogs.deleteModelImage;
const vtPage = viewTexts.pages.modelImageList;

const MessageAreaWrapper = styled.div`
  padding: 20px;
`;

interface Props {
  koumutenId: number;
  company: ICompanyType;
  model: IModelType;
}

export const ModelImageList: FC<Props> = memo(
  ({ koumutenId, company, model }: Readonly<Props>) => {
    const navigator = useNavigator();

    const token = useToken();

    const labelListIsEmpty$ = useDataStream<
      WithPromiseState<
        { latest: boolean },
        { latest: boolean; reason: FetchListErrorReasonType },
        { latest: boolean }
      >
    >(
      pending({ latest: false }),
      modelId$.pipe(
        switchMap(labelListOf),
        map(list => mapListLatest(list, l => l.isEmpty()))
      )
    );

    const imageList$ = useDataStream<ListWithPromiseState<IImageInfoType>>(
      listWithPromiseStateInit<IImageInfoType>(),
      modelId$.pipe(switchMap(modelImageListOf))
    );

    /* states */

    const [annotatedIsVisible, showAll, hideAnnotated] = useBooleanState(true);
    const [IsBulkDelete,InstantDelete,ConfirmDelete] = useBooleanState(false);

    const [
      confirmDeleteDialogIsOpen,
      openConfirmDeleteDialog,
      closeConfirmDeleteDialog
    ] = useBooleanState(false);

    const [
      // dummy comment to control prettier
      imageIdToBeDeleted,
      setImageIdToBeDeleted
    ] = useState<number | undefined>(undefined);

    /* values */

    const annotatedIsVisible$ = useValueAsStream(annotatedIsVisible);
    const imageListFiltered$ = useDataStream<
      ListWithPromiseState<IImageInfoType>
    >(
      listWithPromiseStateInit<IImageInfoType>(),
      combineLatestTyped(imageList$, annotatedIsVisible$).pipe(
        map(([list, annotatedIsVisible]) =>
          mapListLatest(list, l =>
            annotatedIsVisible ? l : l.filter(e => !e.isAnnotated)
          )
        )
      )
    );

    const messageArea = useMemo<{
      status: 'default' | 'warning' | 'alert';
      message: readonly string[];
      showStartTrainingButton: boolean;
    }>(() => toMessageAreaValue(model.status), [model.status]);

    /* handlers */

    const {
      snackbarIsOpen,
      openSnackbar,
      closeSnackbar,
      snackbarState,
      setSnackbarState,
      snackbarMessage,
      setSnackbarMessage
    } = useSnackbar();

    const deleteImage = useCallback(
      (imgId: number) => {
        if (
          token === undefined ||
          imgId === undefined
        ) {
          return;
        }

        setSnackbarState('pending');
        deleteModelImage(token)(model.id, imgId).then(res => {
          openSnackbar();
  
          if (res.type === 'success') {
            fetchModelImageListOf(model.id);
            setSnackbarState('success');
            setSnackbarMessage(snackbarMessages.delete.success);
          } else {
            if (envMode !== 'production') {
              console.error(res.body);
            }
            setSnackbarState('error');
            setSnackbarMessage(snackbarMessages.delete.error);
          }
        });
      },
      [
        token,
        model.id,
        setSnackbarState,
        setSnackbarMessage,
        openSnackbar
      ]
    );

    const onImageClick = useCallback(
      (imgId: number) => {
        navigator(
          routes.modelImageRoot(koumutenId, company.id, model.id, imgId)
        );
      },
      [navigator, koumutenId, company.id, model.id]
    );

    const onDeleteClick = useCallback(
      (imgId: number) => {
        setImageIdToBeDeleted(imgId);
        if(IsBulkDelete){
          deleteImage(imgId)
        }else{
          openConfirmDeleteDialog();
        }
      },
      [openConfirmDeleteDialog,deleteImage,IsBulkDelete]
    );

    const onConfirmDeleteDialogClose = useCallback(
      (yn: 'yes' | 'no') => {
        if (
          yn === 'no' ||
          imageIdToBeDeleted === undefined
        ) {
          closeConfirmDeleteDialog();
          return;
        }
        deleteImage(imageIdToBeDeleted)
        closeConfirmDeleteDialog()
      },
      [
        closeConfirmDeleteDialog,
        deleteImage,
        imageIdToBeDeleted,
      ]
    );

    const {
      startTrainingButtonLabel,
      startTrainingButtonIsDisabled,
      startTrainingButtonStatus,
      onStartTrainingClick
    } = useStartTrainingButtonHandler(model);

    /* extract values */

    const imageList = useStreamValue(imageList$);
    const imageListFiltered = useStreamValue(imageListFiltered$);
    const labelListIsEmpty = useStreamValue(labelListIsEmpty$);

    useLoadingPage(
      imageList?.status === 'pending' ||
        imageListFiltered?.status === 'pending' ||
        labelListIsEmpty?.status === 'pending'
    );

    return (
      <>
        {imageList === undefined ||
        imageListFiltered === undefined ||
        labelListIsEmpty === undefined ? (
          undefined
        ) : imageList.status === promiseState.error ||
          imageListFiltered.status === promiseState.error ? (
          <NotFoundTemplate
            message={viewTexts.notFoundMessages.modelImageList}
          />
        ) : labelListIsEmpty.status === promiseState.error ? (
          <NotFoundTemplate message={viewTexts.notFoundMessages.labelList} />
        ) : labelListIsEmpty.value.latest === true ? (
          <MessageAreaWrapper>
            <MessageArea messages={vtPage.messages.noLabelRegistered} />
          </MessageAreaWrapper>
        ) : (
          <>
            <ModelImageListView
              imageList={imageList.value.latest}
              imageListFiltered={imageListFiltered.value.latest}
              annotatedIsVisible={annotatedIsVisible}
              showAll={showAll}
              hideAnnotated={hideAnnotated}
              IsBulkDelete={IsBulkDelete}
              InstantDelete={InstantDelete}
              ConfirmDelete={ConfirmDelete}
              onImageClick={onImageClick}
              onDeleteClick={onDeleteClick}
              messageArea={messageArea}
              startTrainingButtonLabel={startTrainingButtonLabel}
              startTrainingButtonIsDisabled={startTrainingButtonIsDisabled}
              startTrainingButtonStatus={startTrainingButtonStatus}
              onStartTrainingClick={onStartTrainingClick}
            />
            <ConfirmDeleteDialogTemplate
              title={vt.title}
              messages={vt.message}
              open={confirmDeleteDialogIsOpen}
              onClose={onConfirmDeleteDialogClose}
            />
            <SnackbarForPromise
              isOpen={snackbarIsOpen}
              message={snackbarMessage}
              promiseState={snackbarState}
              onCloseClick={closeSnackbar}
            />
          </>
        )}
      </>
    );
  }
);

ModelImageList.displayName = 'ModelImageList';
